import { Button, Input } from "@devhop/ui";
import { useNavigate } from "@tanstack/react-router";
import { ArrowLeft, HelpCircle, Search } from "lucide-react";
import { useMemo, useState } from "react";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { useCommunicationMinutesQuery } from "../api/queries";
import { CommunicationTour } from "../tour/CommunicationTour";
import { minutesTourSteps } from "../tour/communicationTourSteps";
import { useCommunicationTour } from "../tour/useCommunicationTour";
import { MeetingMinuteRow } from "../ui/MeetingMinuteRow";

export function CommunicationMinutesPage() {
  const [search, setSearch] = useState("");
  const navigate = useNavigate();
  const tour = useCommunicationTour("communication-minutes-tour-completed");
  const { data, isLoading } = useCommunicationMinutesQuery();

  const items = data?.data ?? [];
  const normalizedSearch = search.trim().toLowerCase();
  const filteredItems = useMemo(() => {
    if (!normalizedSearch) return items;
    return items.filter((item) =>
      [item.title, item.summary ?? ""]
        .join(" ")
        .toLowerCase()
        .includes(normalizedSearch),
    );
  }, [items, normalizedSearch]);

  return (
    <>
      <Header />
      <Main>
        <div className="mb-6 flex items-start justify-between gap-3">
          <div data-tourid="communication-minutes-title">
            <div className="flex items-center gap-2">
              <h1 className="font-bold text-2xl tracking-tight">ບັນທຶກກອງປະຊຸມ</h1>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="size-8"
                onClick={tour.startTour}
                aria-label="ເລີ່ມທົດລອງການນຳທາງ"
                title="ເລີ່ມທົດລອງການນຳທາງ"
              >
                <HelpCircle className="size-4" />
              </Button>
            </div>
            <p className="mt-1 text-muted-foreground">
              ລາຍການບັນທຶກການປະຊຸມທີ່ທ່ານສາມາດເຂົ້າເບິ່ງໄດ້.
            </p>
          </div>
          <Button
            type="button"
            variant="outline"
            className="shrink-0"
            onClick={() => navigate({ to: "/app/dashboard" })}
          >
            <ArrowLeft className="size-4" />
            ກັບຄືນ
          </Button>
        </div>

        <div>
          <div className="mb-4">
            <div className="relative w-full max-w-xs">
              <Search className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                className="pl-9"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="ຄົ້ນຫາບັນທຶກ..."
              />
            </div>
          </div>

          <div
            data-tourid="communication-minutes-list"
            className="flex flex-col gap-3"
          >
            {isLoading ? (
              Array.from({ length: 5 }, (_, i) => `minute-skeleton-${i}`).map(
                (key) => (
                  <div
                    key={key}
                    className="h-[4.75rem] animate-pulse rounded-xl border border-border/50 bg-muted/40 sm:h-[5.25rem]"
                  />
                ),
              )
            ) : items.length === 0 ? (
              <p className="py-8 text-center text-muted-foreground">
                ບໍ່ມີບັນທຶກກອງປະຊຸມ.
              </p>
            ) : filteredItems.length === 0 ? (
              <p className="py-8 text-center text-muted-foreground">
                ບໍ່ພົບຂໍ້ມູນຕາມຄຳຄົ້ນຫາ.
              </p>
            ) : (
              filteredItems.map((item) => (
                <MeetingMinuteRow
                  key={item.id}
                  title={item.title}
                  meetingDate={item.meetingDate}
                  summary={item.summary}
                  onOpen={() =>
                    navigate({
                      to: "/app/communication/minutes/$id",
                      params: { id: item.id },
                      search: { from: "minutes" },
                    })
                  }
                />
              ))
            )}
          </div>
        </div>

        <CommunicationTour
          steps={minutesTourSteps}
          run={tour.run}
          onCallback={tour.handleJoyrideCallback}
        />
      </Main>
    </>
  );
}
